import { useEffect, useCallback, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import useBlogStore from "@/store/blog-store";
import { FE_DOMAIN } from "@/lib/configs";
import { PLACEHOLDER_IMG } from "@/lib/constants";
import {
  FaArrowLeft,
  FaFacebook,
  FaLinkedin,
  FaReddit,
  FaTelegram,
} from "react-icons/fa";
import { CiLock } from "react-icons/ci";
import { BiCalendarHeart } from "react-icons/bi";
import "./style.css";
import ErrorState from "./error-state";
import { View, Copy } from "lucide-react";
import { Helmet } from "react-helmet";
import { processLinks } from "@/lib/utils";
import { IoShareSocialSharp } from "react-icons/io5";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Input } from "@/common";
import { Check } from "lucide-react";
import { X } from "lucide-react";

const calculateReadTime = (content) => {
  const wordsPerMinute = 200;
  const textContent = content.replace(/<[^>]*>/g, "");
  const wordCount = textContent.split(/\s+/).length;
  return Math.ceil(wordCount / wordsPerMinute);
};

const BlogDetails = () => {
  const { id } = useParams();
const navigate =useNavigate()
  const { singleBlog, getSingleBlog, loading, error } = useBlogStore();
  const [isCopied, setIsCopied] = useState(false);

  useEffect(() => {
    if (id) getSingleBlog(id);
  }, [id, getSingleBlog]);

  const handleBack = useCallback(() => {
navigate('/blogs')
  }, []);

  const blogUrl = useMemo(
    () => `https://blog.realchaininvest.com/blogs/${id}`,
    [id],
  );
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(blogUrl).then(() => {
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000); // Reset after 2 seconds
    });
  }, [blogUrl]);

  const readTime = useMemo(
    () => (singleBlog ? calculateReadTime(singleBlog.desc || "") : 0),
    [singleBlog],
  );
  const twitterShareUrl = useMemo(() => {
    const text = singleBlog?.title
      ? encodeURIComponent(`Check out this post: ${singleBlog.title}`)
      : encodeURIComponent("Check out this post!");
    const url = encodeURIComponent(blogUrl);
    return `https://twitter.com/intent/tweet?url=${url}&text=${text}`;
  }, [blogUrl, singleBlog?.title]);

  const facebookShareUrl = useMemo(() => {
    const url = encodeURIComponent(blogUrl);
    return `https://www.facebook.com/sharer/sharer.php?u=${url}`;
  }, [blogUrl]);

  const telegramShareUrl = useMemo(() => {
    const text = singleBlog?.title
      ? encodeURIComponent(`Check out this post: ${singleBlog.title}`)
      : encodeURIComponent("Check out this post!");
    const url = encodeURIComponent(blogUrl);
    return `https://t.me/share/url?url=${url}&text=${text}`;
  }, [blogUrl, singleBlog?.title]);

  const linkedinShareUrl = useMemo(() => {
    const url = encodeURIComponent(blogUrl);
    const title = singleBlog?.title ? encodeURIComponent(singleBlog.title) : "";
    const summary = singleBlog?.sub_heading
      ? encodeURIComponent(singleBlog.sub_heading)
      : "";
    return `https://www.linkedin.com/shareArticle?mini=true&url=${url}&title=${title}&summary=${summary}`;
  }, [blogUrl, singleBlog?.title, singleBlog?.sub_heading]);

  const redditShareUrl = useMemo(() => {
    const url = encodeURIComponent(blogUrl);
    const title = singleBlog?.title ? encodeURIComponent(singleBlog.title) : "";
    return `https://www.reddit.com/submit?url=${url}&title=${title}`;
  }, [blogUrl, singleBlog?.title]);

    if (error) {
  getSingleBlog(id);
  }
  if (loading || error ) {
    return (
      <div className="min-h-screen bg-black dark:bg-white">
        <div className="mx-auto max-w-4xl px-4 py-8">
          <div className="">
            <div className="mb-6 h-8 w-1/4 rounded-lg bg-gray-300 dark:bg-gray-700"></div>
            <div className="mb-4 h-12 rounded-lg bg-gray-300 dark:bg-gray-700"></div>
            <div className="mb-8 h-6 w-3/4 rounded-lg bg-gray-300 dark:bg-gray-700"></div>
            <div className="mb-8 h-80 rounded-xl bg-gray-300 dark:bg-gray-700"></div>
            <div className="space-y-4">
              <div className="h-4 w-full rounded bg-gray-300 dark:bg-gray-700"></div>
              <div className="h-4 w-5/6 rounded bg-gray-300 dark:bg-gray-700"></div>
              <div className="h-4 w-4/6 rounded bg-gray-300 dark:bg-gray-700"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }



  if (!singleBlog) {
    return (
      <ErrorState
        message="Blog post not found"
        description="The blog post you're looking for doesn't exist."
        onBack={handleBack}
      />
    );
  }

  return (
    <div className="min-h-screen bg-black dark:bg-white">
      <Helmet>
        {/* --- Standard Meta Tags --- */}
        <title>{singleBlog.meta_title || singleBlog.title}</title>
        <meta
          name="description"
          content={singleBlog.meta_description || singleBlog.sub_heading}
        />
        <meta name="keywords" content={singleBlog.meta_keywords || ""} />

        {/* --- ✅ MAPPING for Custom Meta Tags --- */}
        {singleBlog.custom_meta_tags &&
          singleBlog.custom_meta_tags.map((tag, index) => {
            // Check the 'type' to render the correct attribute: 'name' or 'property'
            if (tag.type === "property") {
              return (
                <meta key={index} property={tag.key} content={tag.content} />
              );
            }

            // Default to rendering the 'name' attribute
            return <meta key={index} name={tag.key} content={tag.content} />;
          })}
      </Helmet>
      <div className="mx-auto max-w-6xl px-4 py-8 pb-0 md:py-8">
        {/* Back and Share Buttons */}
        <div className="flex justify-between">
           
            <button
              onClick={handleBack}
              className="group mb-8 inline-flex items-center gap-2 text-purple-600 transition-colors hover:text-purple-500"
            >
              <FaArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-1" />
              Back to Blog
            </button>
          
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="group mb-8 inline-flex items-center gap-2 text-purple-600 transition-all duration-200 ease-in-out hover:scale-105">
                  <IoShareSocialSharp className="h-4 w-4 transition-transform" />
                  Share
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className="max-h-[85vh] w-[90vw] max-w-sm overflow-y-auto rounded-xl border-gray-800 bg-[#1C1C1E] p-4 text-gray-200 sm:w-80"
                asPortal
              >
                {/* ... your content remains the same */}
                <div className="flex flex-col gap-2">
                  {/* Section 1: Copy Link */}
                  <div>
                    <p className="mb-3 text-sm font-medium text-gray-300">
                      Share this post
                    </p>
                    <div className="flex h-11 gap-3">
                      <Input
                        value={blogUrl}
                        readOnly
                        className="h-11 flex-1 rounded-lg border-0 bg-[#2C2C2E] px-4 text-sm text-gray-200 focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2 focus-visible:ring-offset-[#1C1C1E]"
                      />
                      <Button
                        onClick={handleCopy}
                        className={`flex size-9 shrink-0 items-center justify-center rounded-full text-white transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-[#1C1C1E] ${
                          isCopied
                            ? "bg-green-600 hover:bg-green-700 focus-visible:ring-green-500"
                            : "bg-purple-600 hover:bg-purple-700 focus-visible:ring-purple-500"
                        } `}
                      >
                        {isCopied ? (
                          <Check className="h-5 w-5" />
                        ) : (
                          <Copy className="h-5 w-5" />
                        )}
                      </Button>
                    </div>
                  </div>
                  {/* Section 2: Social Media Buttons */}
                  <div>
                    <p className="mb-3 text-sm font-medium text-gray-300">
                      Share on
                    </p>
                    <div className="flex flex-wrap justify-start gap-3">
                      {" "}
                      {/* Added flex-wrap */}
                      {/* Twitter */}
                      <a
                        href={twitterShareUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex size-9 items-center justify-center rounded-full bg-[#1DA1F2] text-white transition-opacity hover:opacity-90"
                      >
                        <X className="h-5 w-5" />
                      </a>
                      {/* Facebook */}
                      <a
                        href={facebookShareUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex size-9 items-center justify-center rounded-full bg-[#1877F2] text-white transition-opacity hover:opacity-90"
                      >
                        <FaFacebook className="h-5 w-5" />
                      </a>
                      {/* Telegram */}
                      <a
                        href={telegramShareUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex size-9 items-center justify-center rounded-full bg-[#26A5E4] text-white transition-opacity hover:opacity-90"
                      >
                        <FaTelegram className="h-5 w-5" />
                      </a>
                      {/* LinkedIn */}
                      <a
                        href={linkedinShareUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex size-9 items-center justify-center rounded-full bg-[#0A66C2] text-white transition-opacity hover:opacity-90"
                      >
                        <FaLinkedin className="h-5 w-5" />
                      </a>
                      {/* Reddit */}
                      <a
                        href={redditShareUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex size-9 items-center justify-center rounded-full bg-[#FF4500] text-white transition-opacity hover:opacity-90"
                      >
                        <FaReddit className="h-5 w-5" />
                      </a>
                    </div>
                  </div>
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Article Header */}
        <article className="overflow-hidden text-white">
          {/* Hero Image */}
          <div className="relative h-80 overflow-hidden rounded-2xl md:h-96">
            <img
              src={
                singleBlog?.image
                  ? `${FE_DOMAIN}/blogs/${singleBlog?.image}`
                  : PLACEHOLDER_IMG
              }
              alt={singleBlog.title}
              className="h-full w-full rounded-2xl object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 rounded-2xl bg-gradient-to-t from-black/50 to-transparent"></div>
            <div className="absolute bottom-6 left-6 right-6">
              <span className="mb-4 inline-block rounded-full bg-gradient-to-r from-[#ce47ee] to-[#7236cc] px-3 py-1 text-sm font-medium text-white">
                {singleBlog.category?.name || "Uncategorized"}
              </span>
            </div>
          </div>

          <div className="p-8 px-0">
            {/* Title and Subtitle */}
            <h1 className="mb-4 text-3xl font-bold leading-tight text-white dark:text-black md:text-4xl lg:text-5xl">
              {singleBlog.title}
            </h1>

            {singleBlog.sub_heading && (
              <p className="mb-8 text-xl leading-relaxed text-white dark:text-black">
                {singleBlog.sub_heading}
              </p>
            )}

            {/* Meta Information */}
            <div className="mb-8 flex flex-wrap items-center gap-6 text-sm text-white dark:text-black">
              <div className="flex items-center gap-2">
                <BiCalendarHeart className="h-4 w-4" />
                <span>
                  {new Date(singleBlog?.createdAt).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <CiLock className="h-4 w-4" />
                <span>{readTime} min read</span>
              </div>
              <div className="flex items-center gap-2">
                <View className="h-4 w-4" />
                <span>
                  {singleBlog?.views} {singleBlog?.views > 1 ? "Views" : "View"}
                </span>
              </div>
            </div>

            {/* Dynamic Content */}
            <div
              className="prose prose-invert prose-lg blog-content mb-4 text-white"
              dangerouslySetInnerHTML={{
                __html: processLinks(singleBlog?.desc),
              }}
            />
            {/* Updated Date */}
            <div className="mt-8 text-sm text-muted">
              <p>
                Last updated:{" "}
                {new Date(singleBlog.updatedAt).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </p>
            </div>
          </div>
        </article>
      </div>
      <style>
        {`
          .blog-content table {
            width: 100% !important;
          }
          // .blog-content img {
    

          //   margin: auto !important;
          //   padding: 0;
          //   border-radius: 8px;
          // }
         .blog-content th {
            vertical-align: top;
            text-align: left;
            padding: 8px;
          }
          .blog-content table {
            width: 100% !important;
            table-layout: fixed;
            border-collapse: collapse;
          }
          .blog-content table th,
          .blog-content table td {
            vertical-align: top;
            text-align: left;
            padding: 8px;
            word-wrap: break-word;
          }
          // .blog-content table img {
          //   width: 100%;
          //   margin: 10px 0;
          //   padding: 0;
          //   border-radius: 8px;
          // }
          li {
            list-style-type: circle;
            margin-left: 20px;
          }
            @media (max-width: 768px) {
  .blog-content table,
  .blog-content thead,
  .blog-content tbody,
  .blog-content th,
  .blog-content td,
  .blog-content tr {
    display: block;            /* make each element block */
    width: 100% !important;    /* full width */
  }

  .blog-content tr {
    margin-bottom: 1rem;       /* space between rows */
  }


  // .blog-content table img {

  //   display: block;
  //   margin: 0 auto;
  // }
}
  .dark .blog-content {
    color: black;
  }

  .blog-content p{
  align-items:flex-start;
  }
  .blog-content img{
  border-radius:5px;
  }
        `}
      </style>
    </div>
  );
};

export default BlogDetails;
